import type { ReactElement } from 'react';
import type { PageProps } from '../types';
import { GenericPage } from './GenericPage';
import { AgendaPage } from './AgendaPage';
import { AudienceProfilesPage } from './AudienceProfilesPage';
import { BudgetSummaryPage } from './BudgetSummaryPage';
import { CampaignGoalPage } from './CampaignGoalPage';
import { CoverPage } from './CoverPage';
import { CustomPage } from './CustomPage';
import { DataTablePage } from './DataTablePage';
import { SectionDividerPage } from './SectionDividerPage';
import { GeoStrategyPage } from './GeoPage';
import { CampaignArchitecturePage, CustomerJourneyPage } from './JourneyPages';
import { MediaMixPage } from './MediaMixPage';
import { MonthlySchedulePage } from './MonthlySchedulePage';
import { OverviewPage } from './OverviewPage';
import { ProductEducationPage } from './ProductEducationPage';
import { ProductDividerPage } from './ProductPages';
import { ProductPlanPage } from './ProductPlanPage';
import { ProjectionsPage } from './ProjectionsPage';
import { ReportingPage } from './ReportingPage';
import { StatsPage } from './StatsPage';
import { StrategyPage } from './StrategyPages';
import { ThankYouPage, WhyUsPage } from './StaticPages';
import { TacticMomentPage } from './TacticMomentPage';
import { TacticAudiencePage, TacticGroupPage } from './TacticPages';
import { TacticMeasurementPage } from './TacticMeasurementPage';
import { TermsPage } from './TermsPage';

export {
  AgendaPage,
  AudienceProfilesPage,
  BudgetSummaryPage,
  CampaignArchitecturePage,
  CampaignGoalPage,
  CoverPage,
  CustomerJourneyPage,
  CustomPage,
  DataTablePage,
  GeoStrategyPage,
  MediaMixPage,
  MonthlySchedulePage,
  OverviewPage,
  ProductDividerPage,
  ProductEducationPage,
  ProductPlanPage,
  ProjectionsPage,
  ReportingPage,
  SectionDividerPage,
  StatsPage,
  StrategyPage,
  TacticAudiencePage,
  TacticGroupPage,
  TacticMeasurementPage,
  TacticMomentPage,
  TermsPage,
  ThankYouPage,
  WhyUsPage,
};

/**
 * The `momentum_v3` counterpart to `../registry.tsx`'s `PageArchetypeBody`
 * and `../momentum/registry.tsx`'s `MomentumArchetypeBody` - same
 * dispatch-by-section-type shape, momentum_v3's own components (design.md
 * V15 "Web conventions": "own registry `MomentumV3ArchetypeBody`"). Stream
 * AD (Phase 1) landed the five dark slides; Streams AF/AG/AH (Phase 2) add
 * every other type below, including legacy `media_product` mapping to
 * `product_plan` exactly like the standard and momentum_v1 registries
 * already do (design.md slide map: "(legacy) media_product → product_plan,
 * as today"). `GenericPage` is now only the fallback for a section type with
 * no momentum_v3 archetype at all.
 */
export function MomentumV3ArchetypeBody(props: PageProps): ReactElement {
  switch (props.section.type) {
    case 'cover':
      return <CoverPage {...props} />;
    case 'campaign_goal':
      return <CampaignGoalPage {...props} />;
    case 'strategy':
      return <StrategyPage {...props} />;
    case 'product_divider':
      return <ProductDividerPage {...props} />;
    case 'product_education':
      return <ProductEducationPage {...props} />;
    case 'product_plan':
    case 'media_product':
      return <ProductPlanPage {...props} />;
    case 'tactic_measurement':
      return <TacticMeasurementPage {...props} />;
    case 'media_mix':
      return <MediaMixPage {...props} />;
    case 'monthly_schedule':
      return <MonthlySchedulePage {...props} />;
    case 'projections':
      return <ProjectionsPage {...props} />;
    case 'budget_summary':
      return <BudgetSummaryPage {...props} />;
    case 'terms':
      return <TermsPage {...props} />;
    case 'why_us':
      return <WhyUsPage {...props} />;
    case 'thank_you':
      return <ThankYouPage {...props} />;
    case 'geo_strategy':
      return <GeoStrategyPage {...props} />;
    case 'agenda':
      return <AgendaPage {...props} />;
    case 'tactic_moment':
      return <TacticMomentPage {...props} />;
    case 'overview':
      return <OverviewPage {...props} />;
    case 'stats':
      return <StatsPage {...props} />;
    case 'customer_journey':
      return <CustomerJourneyPage {...props} />;
    case 'campaign_architecture':
      return <CampaignArchitecturePage {...props} />;
    case 'audience_profiles':
      return <AudienceProfilesPage {...props} />;
    case 'reporting':
      return <ReportingPage {...props} />;
    case 'tactic_audience':
      return <TacticAudiencePage {...props} />;
    case 'tactic_group':
      return <TacticGroupPage {...props} />;
    case 'data_table':
      return <DataTablePage {...props} />;
    case 'custom_page':
      // docs/custom-page.md "One section type, four layouts": CustomPage
      // dispatches on content.layout itself, including delegating 'table'
      // (and a still-coercing data_table with no layout set) to
      // DataTablePage.
      return <CustomPage {...props} />;
    case 'section_divider':
      return <SectionDividerPage {...props} />;
    default:
      return <GenericPage {...props} />;
  }
}
